import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';

import { get } from '../../../util';
import Modal from '../../components/Modal';
import ModalButtons from '../../components/ModalButtons';

import Icon from '../../../components/Icon/components/Icon';
import { ContainedButton, OutlinedButton } from '../../../components/Buttons';
import { propTypes as i18nPropTypes } from '../../../hocs/intlEquipped';
import ModalHeader from '../../components/ModalHeader';

const FEEDBACK_FORM_NAME = 'FEEDBACK_FORM_NAME';

const CancelSubscriptionFeedbackModal = ({
    onSubmit,
    showNext,
    cancelFlowConfig = [],
    stepIndex,
    showModal,
    onClose,
    theme,
    i18n: { formatMessage },
}) => {
    const [currentReasonKey, setCurrentReasonKey] = useState('');
    const [pending, setPending] = useState(false);
    const [userTextInputValue, setUserTextInputValue] = useState('');
    const { reasons } = get(cancelFlowConfig, [stepIndex, 'content']);
    const title = get(cancelFlowConfig, [stepIndex, 'title']);
    const body = get(cancelFlowConfig, [stepIndex, 'body']);

    const onReasonChange = (event) => {
        setUserTextInputValue('');
        setCurrentReasonKey(event.target.value);
    };

    const getCurrentReason = () => ({
        ...reasons.find(({ reason }) => reason === currentReasonKey),
        userTextInputValue,
    });

    const handleCancelSubscription = () => {
        setPending(true);
        onSubmit(getCurrentReason());
    };

    const handleModalSubmit = () => {
        const currentReason = getCurrentReason();
        const nextReasonStepIndex = 0;
        const nextReasonStep = get(currentReason, ['steps', nextReasonStepIndex]);

        if (!nextReasonStep) {
            handleCancelSubscription();
        } else {
            showNext(nextReasonStep.type, { reason: currentReason, onSubmit, reasonStepIndex: nextReasonStepIndex });
        }
    };

    return (
        <Modal
            open={ showModal }
            theme={ theme }
            onClose={ onClose }
        >
            <ModalHeader
                title={ formatMessage('modals.cancelSubscription.header') }
                onClose={ onClose }
            />
            <div className={ theme.cancelSubscriptionFeedbackModal }>
                <div className={ theme.feedbackContent }>
                    <div className={ theme.info }>
                        {title && <p className={ theme.title }>{title}</p>}
                        {body && <p className={ theme.paragraph }>{body}</p>}
                    </div>
                    <div className={ theme.form }>
                        <FormControl component="fieldset" classes={{ root: theme.feedbackForm }}>
                            <RadioGroup
                                aria-label="reason"
                                name="reason"
                                value={ currentReasonKey }
                                onChange={ onReasonChange }
                            >
                                {reasons.map(({ reason: reasonKey, text }) => (
                                    <FormControlLabel
                                        data-testid={ reasonKey }
                                        key={ reasonKey }
                                        value={ reasonKey }
                                        control={ <Radio /> }
                                        label={ text }
                                        name={ FEEDBACK_FORM_NAME }
                                    />
                                ))}
                                {getCurrentReason()?.allowFreeText && (
                                    <div className={ theme.freeTextField }>
                                        <textarea
                                            data-testid="freetextTextarea"
                                            value={ userTextInputValue }
                                            onChange={ e => setUserTextInputValue(e.target.value) }
                                            placeholder={ formatMessage(
                                                'modals.cancelSubscription.feedbackForm.userInputPlaceholder'
                                            ) }
                                            maxLength={ 255 }
                                        />
                                    </div>
                                )}
                            </RadioGroup>
                        </FormControl>
                    </div>
                </div>
                <ModalButtons theme={ theme }>
                    <ContainedButton
                        text={ formatMessage('modals.cancelSubscription.buttons.cancel') }
                        onClick={ onClose }
                    />
                    <OutlinedButton
                        onClick={ handleModalSubmit }
                        disabled={ pending }
                        className={ classnames({ [theme.pending]: pending }) }
                        text={ formatMessage('modals.cancelSubscription.buttons.feedbackStop') }
                        icon={ pending ? {
                            component: () => <Icon theme={ theme } name="loading" />,
                            position: 'left',
                        } : {} }
                        testId="cancelSubscriptionFeedbackModalBtn"
                    />
                </ModalButtons>
            </div>
        </Modal>
    );
};

CancelSubscriptionFeedbackModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    showModal: PropTypes.bool.isRequired,
    theme: PropTypes.shape({}),
    cancelFlowConfig: PropTypes.arrayOf(PropTypes.shape({
        body: PropTypes.string.isRequired,
        content: PropTypes.shape({
            reasons: PropTypes.arrayOf(PropTypes.shape({
                reason: PropTypes.string.isRequired,
                steps: PropTypes.arrayOf(PropTypes.any).isRequired,
                text: PropTypes.string.isRequired,
            }).isRequired).isRequired,
        }).isRequired,
        title: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
    })).isRequired,
    showNext: PropTypes.func.isRequired,
    stepIndex: PropTypes.number.isRequired,
    i18n: i18nPropTypes,
};

export default CancelSubscriptionFeedbackModal;
